import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import TaskTitles from "./atomic/TaskTitles";
import TaskInput from "./atomic/TaskInput";
import Slider from "./Slider";
import SaveButton from "./atomic/SaveButton";
import AI from "../assets/ai.svg";
import {
  createAIProfile,
  updateAIProfile,
  fetchAIProfileByTask,
} from "../../../Api/aiProfileApi";
import {
  selectAIProfile,
  setAIProfile,
  updateAIProfile as updateProfileAction,
  setLoading,
  setError,
  clearAIProfile,
} from "../../../slice/aiProfileSlice";
import { selectSelectedTask } from "../../../slice/taskSlice";

const SystemPrompts = () => {
  const dispatch = useDispatch();
  const aiProfile = useSelector(selectAIProfile);
  const selectedTask = useSelector(selectSelectedTask);
  // Form state
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    description: "",
    temperature: 0,
    topp: 0,
    task_id: selectedTask.id,
  });

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setLocalError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const { data, loading } = useSelector((state) => state.admin);

  const isAdmin =
    data?.user_role === "SUPER_ADMIN" || data?.user_role === "ADMIN";
  const isSuperAdmin = data?.user_role === "SUPER_ADMIN" || data?.user_role === "ADMIN";
  const userOrganizationId = data?.organization_id;

  // Load AI Profile when task changes
  useEffect(() => {
    const loadAIProfile = async () => {
      if (selectedTask.id) {
        dispatch(setLoading(true));
        try {
          const profile = await fetchAIProfileByTask(selectedTask.id);
          dispatch(setAIProfile(profile));
          console.log(profile.topp);
          setFormData({
            id: profile.id,
            name: profile.name || "",
            description: profile.description || "",
            temperature: parseFloat(profile.temprature),
            topp: parseFloat(profile.topp),
            task_id: profile.task_id,
          });
          setIsEditing(true);
        } catch (err) {
          if (err.response?.status !== 404) {
            const errorMessage =
              err.response?.data?.detail || "Failed to load AI Profile";
            dispatch(setError(errorMessage));
            setLocalError(errorMessage);
          } else {
            dispatch(clearAIProfile());
            resetForm();
          }
        } finally {
          dispatch(setLoading(false));
        }
      } else {
        dispatch(clearAIProfile());
        resetForm();
      }
    };
    loadAIProfile();
  }, [formData.task_id, dispatch]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log("Input change:", name, value);
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: value,
      };
      console.log("New form data:", newData);
      return newData;
    });
  };

  // Handle slider changes
  const handleSliderChange = (e, field) => {
    const value = Number(e.target.value);
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle form submission
  const handleSave = async () => {
    setIsLoading(true);
    setLocalError(null);

    // Check permissions for ADMIN users
    if (isAdmin && !isSuperAdmin) {
      // ADMIN users can only edit system prompts for tasks assigned to their organization
      if (selectedTask.organization_id && selectedTask.organization_id !== userOrganizationId) {
        setLocalError("Sie können nur KI-Profile für Aufgaben Ihrer Organisation bearbeiten");
        setIsLoading(false);
        return;
      }
    }

    // Check if any field has a value
    const hasValue = Object.values(formData).some((value) => {
      if (value === null) return false;
      if (typeof value === "string") return value.trim() !== "";
      return true;
    });

    if (!hasValue) {
      setLocalError("At least one field must have a value");
      setIsLoading(false);
      return;
    }

    // Prepare data for submission
    const submitData = { ...formData };
    // Only include task_id if it has a valid value
    if (submitData.task_id === null) {
      delete submitData.task_id;
    }

    try {
      if (isEditing && formData.id) {
        const updatedProfile = await updateAIProfile(submitData);
        dispatch(updateProfileAction(updatedProfile));
      } else {
        const newProfile = await createAIProfile(submitData);
        dispatch(setAIProfile(newProfile));
      }

      // Reset form after successful save
      resetForm();
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || "Failed to save AI Profile";
      setLocalError(errorMessage);
      dispatch(setError(errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      id: null,
      name: "",
      description: "",
      temperature: 30,
      topp: 30,
      task_id: null,
    });
    setIsEditing(false);
  };

  return (
    <div>
      {/* {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    {error}
                </div>
            )} */}

      <div className="flex justify-between border-b border-gray-200 pb-10">
        <TaskTitles
          title="KI-Profil & Name"
          description="Geben Sie Ihrem KI-Profil einen Namen."
        />
        <div className="w-[60%]">
          <TaskInput
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Geben Sie Ihrem KI-Profil einen Namen."
            title="KI-Profil"
            icon={AI}
            disabled={!isSuperAdmin}
          />
        </div>
      </div>

      <div className="flex justify-between border-b border-gray-200 pb-10 pt-10">
        <TaskTitles
          title="KI-Profil Beschreibung"
          description="Geben Sie Ihre KI-Profilbeschreibung 
ein und stellen Sie wichtige KI-Profil-Parameter ein."
        />
        <div className="w-[60%] flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h1 className="font-bold">Beschreibung</h1>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-200 rounded-lg resize-none h-44"
              placeholder="Geben Sie Ihre KI-Profilbeschreibung 
ein." 
disabled={!isSuperAdmin}
            />
          </div>

          <Slider
            label="Temperature"
            value={formData.temperature}
            onChange={(e) => handleSliderChange(e, "temperature")}
            disabled={!isSuperAdmin}
          />
          <Slider
            label="Top P"
            value={formData.topp}
            onChange={(e) => handleSliderChange(e, "topp")}
            disabled={!isSuperAdmin}
          />
        </div>
      </div>

      <div className="flex justify-end pt-5 pb-10">
        <SaveButton
          onClick={handleSave}
          disabled={isLoading}
          loading={isLoading}
        >
          {isEditing ? "Update" : "Save"}
        </SaveButton>
      </div>
    </div>
  );
};

export default SystemPrompts;
